import React, { useEffect, useState } from "react";
import axios from "axios";
import { Lock, LogOut, Phone, Mail, Users, CalendarDays, Clock } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = ["all", "new", "confirmed", "declined"];

const fmtDate = (d, lang) => {
  try {
    return new Date(`${d}T12:00:00`).toLocaleDateString(lang === "en" ? "en-GB" : "it-IT", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  } catch {
    return d;
  }
};

export default function Admin() {
  const { t, lang } = useLanguage();
  const [pw, setPw] = useState("");
  const [authed, setAuthed] = useState("");
  const [wrong, setWrong] = useState(false);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = (password) => {
    setLoading(true);
    axios
      .get(`${API}/admin/bookings`, { headers: { "x-admin-password": password } })
      .then((r) => setData(r.data))
      .catch(() => toast.error(t.admin.errLoad))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const saved = sessionStorage.getItem("barone_admin_pw");
    if (saved) {
      setAuthed(saved);
      load(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (e) => {
    e.preventDefault();
    try {
      await axios.get(`${API}/admin/bookings`, { headers: { "x-admin-password": pw } });
      sessionStorage.setItem("barone_admin_pw", pw);
      setAuthed(pw);
      setWrong(false);
      load(pw);
    } catch (err) {
      setWrong(err?.response?.status === 401);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const r = await axios.patch(`${API}/admin/bookings/${id}`, { status }, { headers: { "x-admin-password": authed } });
      setData((d) => ({
        ...d,
        bookings: d.bookings.map((b) => (b.id === id ? r.data.booking : b)),
        counts: {
          ...d.counts,
          ...countShift(d.bookings, id, status),
        },
      }));
      toast.success(status === "confirmed" ? "Confermata" : status === "declined" ? "Rifiutata" : "Reimpostata");
    } catch {
      toast.error(t.admin.errLoad);
    }
  };

  const countShift = (bookings, id, status) => {
    const prev = bookings.find((b) => b.id === id)?.status || "new";
    const counts = { new: 0, confirmed: 0, declined: 0 };
    bookings.forEach((b) => (counts[b.status] = (counts[b.status] || 0) + 1));
    counts[prev] -= 1;
    counts[status] += 1;
    return counts;
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <form onSubmit={login} className="w-full max-w-sm rounded-2xl border border-gold/35 bg-warm p-8 shadow-[0_20px_40px_-15px_rgba(59,10,17,0.15)]" data-testid="admin-login-form">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-burgundy">
            <Lock className="w-5 h-5 text-gold" />
          </span>
          <h1 className="mt-5 font-serif text-2xl text-ink">{t.admin.title}</h1>
          <label className="block mt-6 text-[11px] font-mono uppercase tracking-[0.18em] text-stoneMuted" htmlFor="admin-pw">
            {t.admin.password}
          </label>
          <input
            id="admin-pw"
            type="password"
            data-testid="admin-password-input"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-stoneMuted/30 bg-cream px-4 py-3 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/25"
          />
          {wrong && (
            <p className="mt-2 text-xs text-red-700" data-testid="admin-wrong-password-error">
              {t.admin.wrong}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-button"
            className="mt-6 w-full rounded-full bg-burgundy px-6 py-3 text-sm font-semibold text-cream hover:bg-burgundy-light transition-colors"
          >
            {t.admin.login}
          </button>
        </form>
      </div>
    );
  }

  const shown = (data?.bookings || []).filter((b) => filter === "all" || b.status === filter);

  return (
    <div className="min-h-screen bg-cream pb-20" data-testid="admin-panel">
      <div className="bg-burgundy pt-28 pb-10 px-6">
        <div className="max-w-[1100px] mx-auto flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-gold">La Cantina del Barone</p>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl text-cream">{t.admin.title}</h1>
          </div>
          <button
            onClick={() => {
              sessionStorage.removeItem("barone_admin_pw");
              setAuthed("");
              setData(null);
            }}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 rounded-full border border-cream/30 px-5 py-2.5 text-sm text-cream hover:bg-cream/10 transition-colors"
          >
            <LogOut className="w-4 h-4" /> {t.admin.logout}
          </button>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6">
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="admin-counts">
          {["new", "confirmed", "declined", "total"].map((k) => (
            <div key={k} className="rounded-xl border border-gold/30 bg-warm p-4 text-center">
              <p className="font-serif text-3xl text-burgundy">{data?.counts?.[k] ?? "-"}</p>
              <p className="mt-1 text-[11px] font-mono uppercase tracking-[0.18em] text-stoneMuted">{t.admin.counts[k]}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`admin-filter-${f}-button`}
              onClick={() => setFilter(f)}
              className={`rounded-full border px-4 py-1.5 text-xs font-mono uppercase tracking-widest transition-colors ${
                filter === f ? "border-gold bg-gold text-burgundy font-medium" : "border-stoneMuted/30 text-stoneMuted hover:text-ink"
              }`}
            >
              {f === "all" ? t.admin.filterAll : t.admin.counts[f]}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-4" data-testid="admin-bookings-list">
          {loading && <p className="text-sm text-stoneMuted">{t.admin.loading}</p>}
          {!loading && shown.length === 0 && <p className="text-sm text-stoneMuted py-10 text-center">{t.admin.empty}</p>}
          {shown.map((b) => (
            <article key={b.id} className="rounded-xl border border-stoneMuted/20 bg-warm p-5 sm:p-6" data-testid={`admin-booking-card-${b.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-3">
                    <h3 className="font-serif text-xl text-ink">{b.name}</h3>
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest ${
                        b.status === "confirmed"
                          ? "bg-green-100 text-green-800"
                          : b.status === "declined"
                          ? "bg-red-100 text-red-800"
                          : "bg-amber-100 text-amber-900"
                      }`}
                      data-testid={`admin-booking-status-${b.id}`}
                    >
                      {b.status}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-stone-600">
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarDays className="w-4 h-4 text-terracotta" /> {fmtDate(b.date, lang)}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="w-4 h-4 text-terracotta" /> {b.time}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Users className="w-4 h-4 text-terracotta" /> {b.guests}
                    </span>
                  </div>
                  {b.notes && <p className="mt-2 text-sm italic text-stoneMuted">“{b.notes}”</p>}
                  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm">
                    <a href={`mailto:${b.email}`} className="inline-flex items-center gap-1.5 text-burgundy hover:underline" data-testid={`admin-booking-email-${b.id}`}>
                      <Mail className="w-4 h-4" /> {b.email}
                    </a>
                    <a href={`tel:${b.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 text-burgundy hover:underline" data-testid={`admin-booking-phone-${b.id}`}>
                      <Phone className="w-4 h-4" /> {b.phone}
                    </a>
                  </div>
                </div>
                <div className="flex gap-2">
                  {b.status !== "confirmed" && (
                    <button
                      onClick={() => updateStatus(b.id, "confirmed")}
                      data-testid={`admin-confirm-booking-${b.id}`}
                      className="rounded-full bg-burgundy px-4 py-2 text-xs font-semibold text-cream hover:bg-burgundy-light transition-colors"
                    >
                      {t.admin.confirm}
                    </button>
                  )}
                  {b.status !== "declined" && (
                    <button
                      onClick={() => updateStatus(b.id, "declined")}
                      data-testid={`admin-decline-booking-${b.id}`}
                      className="rounded-full border border-stoneMuted/30 px-4 py-2 text-xs font-medium text-ink hover:border-red-400 hover:text-red-700 transition-colors"
                    >
                      {t.admin.decline}
                    </button>
                  )}
                  {b.status !== "new" && (
                    <button
                      onClick={() => updateStatus(b.id, "new")}
                      data-testid={`admin-reset-booking-${b.id}`}
                      className="rounded-full border border-stoneMuted/30 px-4 py-2 text-xs font-medium text-stoneMuted hover:text-ink transition-colors"
                    >
                      {t.admin.reset}
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
